import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import BaseMatManModal from '@monorepo/shared/src/components/common/Modals/BaseMatManModal';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import useAttemptStore from '@monorepo/shared/src/graph/state/query-hooks/use-attempt-store';
import theme, {
  FONT_GSS_FUTURA_BOOK,
  FONT_GSS_FUTURA_SEMI_BOLD,
} from '@monorepo/shared/src/themes/defaultTheme';
import {
  remCalc,
  translate,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { ActivityNames, ActivityTypes } from '@monorepo/shared/src/types';
import { AttemptStatus } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import FileUploadInput from 'components/modules/Reports/components/common/FileUploadInput';
import UseAssignmentStore from 'graph/state/query-hooks/use-assignments-store';
import { getActivityAssignment } from 'components/modules/Reports/utils/reports-util';

const t = (key: string, options?) => translate('reports', key, options);

const IMG_EXTENSIONS = ['.jpg', '.jpeg', '.pdf', '.png'];
const MAX_FILE_SIZE = 5242880;

const useFileUpload = ({
  student,
  character,
  openFileUploadModal,
  handleClose,
  setOpenFileUploadModal,
}) => {
  const classes = useStyles();
  const [, { openModal, closeModal }] = useModal();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadedFile, setFile] = useState<HTMLInputElement | null>(null);

  const [, { handleSaveAttempt }] = useAttemptStore();
  const [{ assignments }, { refetch: assignmentsRefetch }] = UseAssignmentStore();

  let file;

  const activityAssignment = getActivityAssignment(
    assignments,
    character,
    ActivityTypes.PRACTICE_BOOK
  );

  const hasExtension = (fileName) => {
    const pattern = '(' + IMG_EXTENSIONS.join('|').replace(/\./g, '\\.') + ')$';
    return new RegExp(pattern, 'i').test(fileName);
  };

  const handleExit = () => {
    setOpenFileUploadModal(false);
    closeModal();
    setErrorMessage('');
  };

  const handleFileUpload = (e) => {
    setErrorMessage('');
    const fileToUpload = e.target.files[0];
    if (hasExtension(fileToUpload.name) && fileToUpload.size < MAX_FILE_SIZE) {
      file = fileToUpload;
      setFile(file);
    } else {
      setErrorMessage('File is too large or wrong file type.');
      setFile(null);
    }
  };

  const handleAttemptUpdate = async () => {
    setLoading(true);
    const result =
      (file || uploadedFile) &&
      activityAssignment &&
      student.id &&
      (await handleSaveAttempt(
        {
          assignmentActivityId: activityAssignment.id,
          status: AttemptStatus.COMPLETED,
          dateCompleted: new Date(),
          studentId: student.id,
        },
        {
          description: `Assignment for letter ${character} - ${ActivityNames.PRACTICE_BOOK}`,
          file: file ?? uploadedFile,
        }
      ));
    if (!result || result.error) {
      setLoading(false);
      setErrorMessage('There was an error uploading your file.');
      return;
    }
    setOpenFileUploadModal(false);
    closeModal();
    handleClose();
    setLoading(false);
    assignmentsRefetch();
  };

  useEffect(() => {
    openFileUploadModal &&
      openModal({
        Title: () => <BaseModalTitle text={t('uploadFiles')} onClose={handleExit} />,
        body: (
          <BaseMatManModal
            leftButtonProps={{
              onClick: handleExit,
              text: t('cancel'),
            }}
            rightButtonProps={{
              text: t('uploadFiles'),
              startIcon: faUpload,
              disabled: loading,
              onClick: handleAttemptUpdate,
            }}
          >
            <>
              <Typography className={classes.modalHeader}>{t('uploadFilesHeader')}</Typography>
              <Typography className={classes.modalText}>
                {t('uploadFilesBody')}
                <strong>{student.name}</strong>
              </Typography>
              <FileUploadInput
                label={'Practice book files'}
                accept="image/gif,image/jpeg,application/pdf,image/png"
                multiple={false}
                onChange={handleFileUpload}
              />
              {errorMessage && <Typography className={classes.error}>{errorMessage}</Typography>}
            </>
          </BaseMatManModal>
        ),
      });
  }, [openFileUploadModal, errorMessage, loading]);
};

const useStyles = makeStyles(() => ({
  modalHeader: {
    fontSize: remCalc(24),
    lineHeight: remCalc(36),
    marginBottom: remCalc(12),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
  },
  modalText: {
    fontSize: remCalc(18),
    lineHeight: remCalc(28),
    marginBottom: remCalc(12),
    fontFamily: FONT_GSS_FUTURA_BOOK,
  },
  error: {
    fontSize: remCalc(16),
    color: theme.palette.error.main,
  },
}));

export default useFileUpload;
